import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = toCamelCase(obj?.[key]);
  }
  return camelObj;
}

// Helper function to convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }
  
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = toSnakeCase(obj?.[key]);
  }
  return snakeObj;
}

// Helper to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const flightService = {
  /**
   * Create flight information for a trip
   */
  async create(flightData) {
    try {
      const snakeData = toSnakeCase(flightData);
      const { data, error } = await supabase
        ?.from('flight_information')
        ?.insert(snakeData)
        ?.select()
        ?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Create flight info error:', error);
      throw error;
    }
  },

  /**
   * Get flight information by trip ID
   */
  async getByTripId(tripId) {
    try {
      const { data, error } = await supabase
        ?.from('flight_information')
        ?.select('*')
        ?.eq('trip_id', tripId)
        ?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return null;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Get flight info error:', error);
      throw error;
    }
  },

  /**
   * Update flight information
   */
  async update(id, updates) {
    try {
      const snakeUpdates = toSnakeCase(updates);
      const { data, error } = await supabase
        ?.from('flight_information')
        ?.update(snakeUpdates)
        ?.eq('id', id)
        ?.select()
        ?.single();
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }
      
      return toCamelCase(data);
    } catch (error) {
      console.error('Update flight info error:', error);
      throw error;
    }
  },

  /**
   * Delete flight information
   */
  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('flight_information')
        ?.delete()
        ?.eq('id', id);
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Delete flight info error:', error);
      throw error;
    }
  },

  /**
   * Upload flight ticket file
   */
  async uploadTicket(file, userId, tripId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${userId}/${tripId}-${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase?.storage
        ?.from('flight-tickets')
        ?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Upload ticket error:', error);
        return null;
      }
      
      return data?.path;
    } catch (error) {
      console.error('Upload ticket error:', error);
      return null;
    }
  },

  /**
   * Get signed URL for flight ticket
   */
  async getTicketUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('flight-tickets')
        ?.createSignedUrl(filePath, 3600);
      
      if (error) {
        console.error('Get ticket URL error:', error);
        return null;
      }
      
      return data?.signedUrl;
    } catch (error) {
      console.error('Get ticket URL error:', error);
      return null;
    }
  },

  /**
   * Delete flight ticket file
   */
  async deleteTicket(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from('flight-tickets')
        ?.remove([filePath]);
      
      if (error) {
        console.error('Delete ticket error:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Delete ticket error:', error);
      return false;
    }
  },

  /**
   * Get flight tracking logs
   */
  async getTrackingLogs(flightInfoId) {
    try {
      const { data, error } = await supabase
        ?.from('flight_tracking_logs')
        ?.select('*')
        ?.eq('flight_info_id', flightInfoId)
        ?.order('created_at', { ascending: false });
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }
      
      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get tracking logs error:', error);
      throw error;
    }
  }
};